(() => {
  if (customElements.get('giesto-editorial-header')) return;
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  class EditorialHeader extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.querySelectorAll('.geh-language [data-giesto-language-code]').forEach(link => {
        const url = new URL(location.href);
        const path = url.pathname.replace(/^\/(?:en|tr)(?=\/|$)/, '') || '/';
        url.pathname = (link.dataset.giestoLanguageCode === 'en' ? '/en' : '') + path;
        link.href = url.href;
      });
      this.menu = this.querySelector('.geh-menu');
      this.scroll = this.querySelector('.geh-menu__scroll');
      this.category = this.querySelector('.geh-header [data-geh-category][aria-current]')?.dataset.gehCategory || 'new';
      // The menu artwork is lazy inside a closed dialog, so nothing downloads
      // until the menu opens. Each frame shows a skeleton until its image
      // arrives, and downloads start as soon as a visitor reaches for the menu
      // through pointer or keyboard intent.
      this.menu.setAttribute('data-skeleton', '');
      this.menu.querySelectorAll('img').forEach(img => {
        const loaded = () => img.setAttribute('data-loaded', '');
        if (img.complete && img.naturalWidth) loaded();
        else {
          img.addEventListener('load', loaded, { once: true });
          img.addEventListener('error', loaded, { once: true });
        }
      });
      this.querySelectorAll('[data-geh-open]').forEach(trigger => {
        const warm = () => this.warmMenu(trigger.dataset.gehOpen || this.category);
        for (const type of ['pointerenter', 'pointerdown', 'focus']) trigger.addEventListener(type, warm, { once: true, passive: true });
      });
      this.homeNav = this.querySelector('.geh-home-nav__columns');
      this.scrollHints = [...this.querySelectorAll('.geh-categories')].map(list => {
        const hint = document.createElement('span');
        hint.className = 'geh-scroll-hint';
        hint.textContent = '•••';
        hint.setAttribute('aria-hidden', 'true');
        hint.hidden = true;
        list.parentElement.classList.add('geh-secondary-window');
        list.classList.add('geh-secondary-scroll');
        list.after(hint);
        return { list, hint };
      });
      this.menu.querySelectorAll('[data-geh-category]').forEach(control => {
        const reveal = () => {
          if (this.menu.open) {
            const category = control.dataset.gehCategory;
            this.setCategory(category);
            if (category === 'new' || category === 'basic') {
              this.scroll.scrollTop = 0;
              this.menu.scrollTop = 0;
            }
          }
        };
        control.addEventListener('pointerenter', () => {
          if (matchMedia('(hover: hover)').matches) reveal();
        });
        control.addEventListener('focus', reveal);
      });
      if (this.homeNav) {
        this.homeNav.querySelectorAll('[data-geh-category]').forEach(control => {
          control.addEventListener('pointerenter', () => {
            if (matchMedia('(hover: hover)').matches) this.setHomeCategory(control.dataset.gehCategory);
          });
          control.addEventListener('focus', () => this.setHomeCategory(control.dataset.gehCategory));
        });
        this.homeNav.addEventListener('pointerleave', () => this.clearHomeCategory());
        this.homeNav.addEventListener('focusout', () => {
          requestAnimationFrame(() => {
            if (!this.homeNav.contains(document.activeElement)) this.clearHomeCategory();
          });
        });
      }
      this.addEventListener('click', event => {
        const open = event.target.closest('[data-geh-open]');
        if (open) { event.preventDefault(); this.openMenu(open.dataset.gehOpen || this.category, open); return; }
        if (event.target.closest('[data-geh-close]')) { this.closeMenu(); return; }
      });
      this.menu.addEventListener('cancel', event => { event.preventDefault(); this.closeMenu(); });
      this.menu.addEventListener('close', () => {
        clearTimeout(this.closeTimer);
        this.menu.removeAttribute('data-open');
        // Scroll restoration is synchronous in finishClose, before another drawer opens.
        this.querySelector('[data-testid="header-navigation-menu-button"]').setAttribute('aria-expanded', 'false');
        if (this.returnFocus !== false) this.menuTrigger?.focus({ preventScroll: true });
      });
      const heroes = [...document.querySelectorAll('.ge-hero')];
      const motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      const update = () => {
        this.alignCategoryLists();
        const fixedHeader = this.querySelector('.geh-header');
        const numberedRail = document.querySelector('#header-group > .gnr');
        const railVisible = numberedRail && getComputedStyle(numberedRail).visibility !== 'hidden';
        const visibleHeight = (fixedHeader?.offsetHeight || 0) + (railVisible ? numberedRail.offsetHeight : 0);
        const headerHeight = visibleHeight || parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--geh-chrome')) || 0;
        for (const name of ['--header-height', '--header-group-height']) if (document.body.style.getPropertyValue(name) !== `${headerHeight}px`) document.body.style.setProperty(name, `${headerHeight}px`);
        if (!this.hasAttribute('data-home')) return;
        const footer = document.querySelector('.ge-footer--canonical');
        this.toggleAttribute('data-footer-visible', Boolean(footer && footer.getBoundingClientRect().top < innerHeight * .8));
        this.updateScrollHints();
        for (const hero of heroes) {
          // Measure the stationary section, never its scaled inner surface.
          const rect = hero.getBoundingClientRect();
          const gone = -rect.top / rect.height;
          const progress = motionQuery.matches ? 0 : Math.max(0, Math.min(1, (gone - .2) / .8));
          // A confident pullback early in the exit, easing gently toward its end.
          // The image travels independently inside the frame for photographic depth.
          const eased = 1 - Math.pow(1 - progress, 3);
          const set = (name, value) => { if (hero.style.getPropertyValue(name) !== value) hero.style.setProperty(name, value); };
          set('--ge-exit-scale', String(1 - .2 * eased));
          set('--ge-exit-lift', `${-32 * eased}px`);
          set('--ge-exit-tilt', `${2.6 * eased}deg`);
          set('--ge-media-scale', String(1 + .12 * eased));
          set('--ge-media-drift', `${rect.height * .035 * eased}px`);
          set('--ge-caption-lift', `${-38 * eased}px`);
          set('--ge-caption-opacity', String(1 - .65 * eased));
          set('--ge-shadow-y', `${18 * eased}px`);
          set('--ge-shadow-blur', `${48 * eased}px`);
          set('--ge-shadow-opacity', String(.18 * eased));
          set('--ge-wash-opacity', String(.12 * eased));
        }
        const at = Math.min(120, innerHeight * .1);
        const section = [...document.querySelectorAll('[data-ge-tone]')].find(el => { const r = el.getBoundingClientRect(); return r.top <= at && r.bottom > at; });
        this.dataset.tone = section?.dataset.geTone || 'light';
        this.toggleAttribute('data-framed', Boolean(section?.matches('.ge-hero') && Number(section.style.getPropertyValue('--ge-exit-scale')) < .999));
        const map = { 'ge-new': 'new', 'ge-basic': 'basic', 'ge-active': 'active', 'ge-sale': 'sale' };
        if (!this.menu.open && map[section?.id]) this.setCategory(map[section.id], false);
      };
      let pending = false;
      const schedule = () => { if (!pending) { pending = true; requestAnimationFrame(() => { pending = false; update(); }); } };
      // Desktop Shopify scrolls .page-wrapper; the local/mobile page scrolls
      // the document. Capture scroll events from either without changing motion.
      window.addEventListener('scroll', schedule, { passive: true, capture: true });
      window.addEventListener('resize', schedule, { passive: true });
      document.addEventListener('giesto:catalog-header-change', schedule);
      motionQuery.addEventListener('change', schedule);
      // Let navigation tear down the native dialog itself. Closing it during
      // pagehide mutates the top layer while the document is being discarded.
      window.addEventListener('pagehide', () => clearTimeout(this.closeTimer));
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.closeMenu({ immediate: true, restoreFocus: false });
      });
      update();
    }
    setHomeCategory(category) {
      if (!this.homeNav) return;
      this.homeNav.dataset.category = category;
      this.homeNav.querySelectorAll('[data-geh-home-links]').forEach(list => {
        list.hidden = list.dataset.gehHomeLinks !== category;
      });
      this.alignCategoryLists();
    }
    alignCategoryLists() {
      const align = (columns, category, attribute) => {
        if (!columns || !category || !columns.getClientRects().length) return;
        const control = columns.querySelector(`.geh-primary [data-geh-category="${category}"]`);
        const list = columns.querySelector(`[${attribute}="${category}"]:not([hidden])`);
        const firstLink = list?.querySelector('a');
        if (!control || !firstLink) return;
        const column = list.parentElement;
        const primary = control.getBoundingClientRect();
        const secondary = firstLink.getBoundingClientRect();
        const offset = primary.top - column.getBoundingClientRect().top + (primary.height - secondary.height) / 2;
        column.style.setProperty('--geh-category-offset', `${Math.max(0, offset)}px`);
      };
      align(this.homeNav, this.homeNav?.dataset.category, 'data-geh-home-links');
      if (this.menu.open) {
        this.menu.querySelectorAll('.geh-menu__columns').forEach(columns => {
          align(columns, this.menu.dataset.category, 'data-geh-links');
        });
      }
      this.updateScrollHints();
    }
    updateScrollHints() {
      for (const { list, hint } of this.scrollHints) {
        if (list.closest('dialog') && !this.menu.open) { if (!hint.hidden) hint.hidden = true; continue; }
        const visible = list.getClientRects().length && getComputedStyle(list).visibility !== 'hidden' && (!list.closest('dialog') || this.menu.open);
        hint.hidden = true;
        if (!visible) continue;
        const links = [...list.querySelectorAll('a')];
        if (!links.length) continue;
        const rect = list.getBoundingClientRect();
        const first = links[0].getBoundingClientRect();
        const rowHeight = first.height;
        const step = links.length > 1 ? links[1].getBoundingClientRect().top - first.top : rowHeight;
        if (step <= 0) continue;
        // Keep a proportional lower margin and reserve one complete row for
        // the ellipsis. Recalculate on resize, category changes and scrolling.
        const region = list.closest('.geh-home-nav, .geh-menu__aside, .geh-menu__scroll');
        const regionBottom = region?.matches('.geh-home-nav')
          ? region.getBoundingClientRect().top + parseFloat(getComputedStyle(region).maxHeight)
          : region?.getBoundingClientRect().bottom || innerHeight;
        const bottom = Math.min(innerHeight, regionBottom) - Math.max(16, innerHeight * .04);
        const available = Math.max(rowHeight, bottom - rect.top);
        const rows = links.map(link => link.getBoundingClientRect());
        const naturalHeight = rows.at(-1).bottom - first.top;
        const overflowing = naturalHeight > available + 1;
        const budget = available - (overflowing ? step : 0);
        const lastVisible = rows.filter(row => row.bottom - first.top <= budget + 1).at(-1) || first;
        const height = lastVisible.bottom - first.top;
        list.style.maxHeight = `${height}px`;
        list.parentElement.style.paddingBottom = overflowing ? `${step}px` : '0px';
        const style = getComputedStyle(list);
        for (const property of ['fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight']) hint.style[property] = style[property];
        hint.style.fontSize = `${Math.max(16, parseFloat(style.fontSize) * 1.15)}px`;
        hint.style.letterSpacing = '2px';
        hint.style.top = `${height + step - rowHeight}px`;
        hint.style.height = `${rowHeight}px`;
        hint.hidden = !overflowing || list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
      }
    }

    clearHomeCategory() {
      if (!this.homeNav) return;
      delete this.homeNav.dataset.category;
      this.homeNav.querySelectorAll('[data-geh-home-links]').forEach(list => { list.hidden = true; });
      this.updateScrollHints();
    }
    setCategory(category, panel = true) {
      this.category = category;
      this.querySelectorAll('[data-geh-category]').forEach(button => {
        if (button.dataset.gehCategory === category) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      if (panel && (category === 'new' || category === 'basic')) {
        this.warmMenu(category);
        this.menu.querySelectorAll('[data-geh-story]').forEach(story => { story.hidden = story.dataset.gehStory !== category; });
      }
      if (!panel || this.menu.dataset.category === category) return;
      this.querySelectorAll('[data-geh-links]').forEach(list => { list.hidden = list.dataset.gehLinks !== category; });
      this.menu.dataset.category = category;
      this.alignCategoryLists();
    }
    // Starts the downloads for the tiles and a category's story. The first
    // screen is the tiles, heading and landscape photograph the menu opens on,
    // plus the portrait that the two-column layout raises into view.
    warmMenu(category = 'new', { firstScreen = false } = {}) {
      const story = this.menu.querySelector(`[data-geh-story="${category}"]`) || this.menu.querySelector('[data-geh-story]:not([hidden])');
      const firstScreenImages = '.ge-story__heading img, .ge-story__landscape img' + (matchMedia('(min-width: 750px)').matches ? ', .ge-story__portrait img' : '');
      const first = [...this.menu.querySelectorAll('.geh-tiles img'), ...(story?.querySelectorAll(firstScreenImages) || [])];
      const rest = firstScreen ? [] : [...(story?.querySelectorAll('img') || [])].filter(img => !first.includes(img));
      // A visitor reaching for the menu needs the first screen before the
      // photographs further down, so those wait behind it. An idle warm-up
      // keeps the browser's default priority so it never competes with the page.
      const start = (img, priority) => {
        if (img.loading !== 'lazy') return;
        if (priority) img.fetchPriority = priority;
        img.loading = 'eager';
      };
      first.forEach(img => start(img, firstScreen ? '' : 'high'));
      rest.forEach(img => start(img, 'low'));
    }
    openMenu(category = 'new', trigger) {
      clearTimeout(this.closeTimer);
      this.clearHomeCategory();
      this.setCategory(category);
      this.returnFocus = true;
      if (this.menu.open) { this.menu.setAttribute('data-open', ''); return; }
      this.menuTrigger = trigger || this.querySelector('[data-testid="header-navigation-menu-button"]');
      this.previousOverflow = document.documentElement.style.overflow;
      this.pageScroller = document.querySelector('.page-wrapper');
      this.previousPageOverflow = this.pageScroller?.style.overflow;
      if (this.pageScroller) this.pageScroller.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      this.menu.showModal();
      this.alignCategoryLists();
      this.scroll.scrollTop = 0;
      this.menu.scrollTop = 0;
      this.querySelector('[data-testid="header-navigation-menu-button"]').setAttribute('aria-expanded', 'true');
      this.menu.querySelector('[data-geh-close]').focus({ preventScroll: true });
      requestAnimationFrame(() => requestAnimationFrame(() => { if (this.menu.open) this.menu.setAttribute('data-open', ''); }));
    }
    closeMenu({ immediate = false, restoreFocus = true } = {}) {
      clearTimeout(this.closeTimer);
      this.returnFocus = restoreFocus;
      this.querySelectorAll('[data-giesto-splash-dialog][open]').forEach(dialog => dialog.close());
      if (!this.menu.open) return;
      this.menu.removeAttribute('data-open');
      const finishClose = () => {
        document.documentElement.style.overflow = this.previousOverflow ?? '';
        if (this.pageScroller) this.pageScroller.style.overflow = this.previousPageOverflow ?? '';
        this.menu.close();
      };
      if (immediate || reduceMotion()) finishClose();
      else this.closeTimer = setTimeout(finishClose, 450);
    }
  }
  customElements.define('giesto-editorial-header', EditorialHeader);

  // One player loads only the campaign file for the current viewport.
  class CampaignVideo extends HTMLElement {
    // An optional AV1 encode looks far sharper than H.264 at the same file size;
    // the plain data-*-src stays the H.264 fallback every browser can play.
    source(variant) {
      const av1 = this.dataset[variant + 'Av1Src'];
      if (av1 && this.video.canPlayType('video/mp4; codecs="av01.0.08M.10"') === 'probably') return av1;
      return this.dataset[variant + 'Src'];
    }
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.mobile = matchMedia('(max-width: 749px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = false;
      this.update = () => {
        this.video.muted = true;
        this.video.defaultMuted = true;
        this.video.volume = 0;
        if (this.mobile.matches && this.hasAttribute('data-mobile-image')) {
          this.video.pause();
          this.removeAttribute('data-playing');
          if (this.video.hasAttribute('src')) {
            this.video.removeAttribute('src');
            this.video.load();
          }
          return;
        }
        if (this.motion.matches || navigator.connection?.saveData) {
          this.video.pause();
          this.removeAttribute('data-playing');
          return;
        }
        if (!this.visible || document.hidden) {
          this.video.pause();
          return;
        }
        const variant = this.mobile.matches ? 'mobile' : 'desktop';
        const requested = this.source(variant);
        const src = this.failedSource === requested ? this.dataset[variant + 'FallbackSrc'] || requested : requested;
        if (this.video.getAttribute('src') !== src) {
          this.removeAttribute('data-playing');
          this.video.poster = this.dataset[variant + 'Poster'];
          this.video.src = src;
          this.video.muted = true;
          this.video.load();
        }
        this.video.play().catch(() => { this.removeAttribute('data-playing'); });
      };
      this.onPlaying = () => { this.setAttribute('data-playing', ''); };
      this.onError = () => {
        this.removeAttribute('data-playing');
        const variant = this.mobile.matches ? 'mobile' : 'desktop';
        const fallback = this.dataset[variant + 'FallbackSrc'];
        if (fallback && this.video.getAttribute('src') !== fallback) { this.failedSource = this.video.getAttribute('src'); this.video.src = fallback; this.video.load(); if (this.visible) this.video.play().catch(() => {}); }
      };
      this.video.addEventListener('playing', this.onPlaying);
      this.video.addEventListener('error', this.onError);
      this.mobile.addEventListener('change', this.update);
      this.motion.addEventListener('change', this.update);
      document.addEventListener('visibilitychange', this.update);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= .2;
        this.update();
      }, { threshold: [0, .2] });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.observer.disconnect();
      this.video.pause();
      this.video.removeEventListener('playing', this.onPlaying);
      this.video.removeEventListener('error', this.onError);
      this.mobile.removeEventListener('change', this.update);
      this.motion.removeEventListener('change', this.update);
      document.removeEventListener('visibilitychange', this.update);
      this.ready = false;
    }
  }
  customElements.define('giesto-campaign-video', CampaignVideo);

  // The sale rail sits above its section link so it can scroll; pass plain
  // clicks on the cards through to that link so they still open the sale.
  document.querySelectorAll('.ge-hero--sale .ge-sale-rail').forEach(rail => {
    const link = rail.closest('.ge-hero')?.querySelector('.ge-hero__image-link');
    if (!link) return;
    rail.addEventListener('click', event => {
      if (event.target.closest('a, button')) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey) window.open(link.href, '_blank', 'noopener');
      else link.click();
    });
  });
})();
